import React, { useState, useEffect, useContext } from "react"
import { Formik, Form } from "formik"
import { useNavigate, useLocation } from "react-router-dom"
import * as Yup from "yup"

import _ from "lodash"
import { useMutation } from "@apollo/client"

//Formik helpers
import FormikTextInput from "../../../../../helpers/formikInputs/FormikTextInput"

//gsql queries
import { ADD_ORGANIZATION, EDIT_ORGANIZATION } from "../../queries"

//mapbox
import "mapbox-gl/dist/mapbox-gl.css"

// Context
import { UserContext } from "../../../../../context/userContext"

//Referal modules
import RefTextInput from "../../Input/RefTextInput"
import RefCheckBox from "../../Input/RefCheckboxInput"
import RefDropDown from "../../Input/RefDropDown"
import RefGetErrorMessage from "../../../../Referrals/Components/RefGetErrorMessage"
import ReferralEntityFormLocations from "./ReferralEntityFormLocations"
import {
  partnerDefaultEnterpriseId,
  partnerDefaultOrganizationType,
} from "../../../../../config/referralsSettings"
import { getServiceCategoryNode } from "../../sharedFunctions"
import ReferralEntityFormChangeRequests from "./ReferralEntityFormChangeRequests"

//Referral API
import {
  refApiReferralEntityCreate,
  refApiReferralEntityUpdate,
  referralsApiReferralEntityGet,
  refApiLocationTypesGet,
  refApiServiceCategoriesGet,
  refApiReferralEntityChangeRequest,
  refApiReferralEntityValidate,
  referralsApiReferralEntityGetByOrganization,
} from "../../../partnerAPI"

//PrimeReact
import { Button } from "primereact/button"
import { TabView, TabPanel } from "primereact/tabview"
import { Badge } from "primereact/badge"
import { Tooltip } from "primereact/tooltip"

//Routes
import ROUTES from "../../../../../components/Routes/routesContants"

//Style
import styles from "../../../../../styles/Form-Inputs.module.css"
import refEntityStyles from "../../../../../styles/referrals/ReferralEntityForm.module.css"
import { LogError } from "../../../../../helpers/logger"
import ReferralEntityUserList from "../../Users/ReferralEntityUserList"
import ReferralEntityFormChangeLog from "./ReferralEntityFormChangeLog"
import {
  getAddOrganizationVariables,
  getSOAROrganizationName,
} from "./ReferralEntityFormFunctions"
import ReferralEntityFormValidationDialog from "./ReferralEntityFormValidationDialog"
import ReferralEntityFormNameCheck from "./ReferralEntityFormNameCheck"

export default function ReferralEntityForm({
  showChangeRequests,
  onCancelClick,
  onEntityCreated,
  hideHeader,
  hideCancelButton,
  referralEntityId,
  triggerRefresh,
}) {
  const entityId = referralEntityId
  const location = useLocation()
  const navigate = useNavigate()
  const userCtx = useContext(UserContext)

  const isPartnerUser = [9, 10].some(
    (id) => id === userCtx.session.user.staff_role
  )
  const isEntityAdmin = userCtx.session.user.staff_role === 9
  const changeRequestPanelIndex = 3

  //Props

  const [selectedLocation, setSelectedLocation] = useState(-1)
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [doDisableForm, setDoDisableForm] = useState(false)
  const [locationTypes, setLocationTypes] = useState([])
  const [serviceCategories, setServiceCategories] = useState([])
  const [reportedInvalidForm, setReportedInvalidForm] = useState(true)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [refreshProfile, setRefreshProfile] = useState(false)
  const [changeLog, setChangeLog] = useState(undefined)
  const [validationProfile, setValidationProfile] = useState(undefined)
  const [showValidationDialog, setShowValidationDialog] = useState(false)

  const [referralEntity, setReferralEntity] = useState({
    referralEntity: {
      id: undefined,
      name: "",
      organizationId: "",
      fein: "",
      noPrimaryPhone: false,
      primaryPhone: "",
      DefaultUserId: "",
      noPrimaryEmail: false,
      primaryEmail: "",
      primaryContactName: "",
      primaryLocationId: -1,
      notificationsEnabled: true,
      dateCreated: undefined,
      entityStatus: undefined,
      entityStatusUpdated: undefined,
      latitude: 0,
      longitude: 0,
      creationOrganizationId: userCtx.session.user.organization_id,
    },
    referralLocations: [
      {
        location: {
          id: -1,
          referralEntityId: undefined,
          name: "Primary",
          noAddress: false,
          country: "United States",
          address: "",
          city: "",
          state: "",
          zipcode: "",
          noPhone: false,
          phone: "",
          noEmail: false,
          email: "",
          status: "Active",
          type: "",
          website: "",
          virtual: false,
        },
        serviceTypes: [],
        serviceTypeSelection: undefined,
      },
    ],
    referralCategories: [],
    userIds: [],
  })

  const isNew = !referralEntity.referralEntity.id
  const entityIsVerified =
    referralEntity.referralEntity.entityStatus === "confirmed"
  const saveChangeRequest = entityIsVerified && !isPartnerUser && !isNew

  const [canEdit, setCanEdit] = useState(isNew || !entityId)

  const profileStateOptions = [
    { label: "Unconfirmed", value: "unconfirmed" },
    { label: "Under review", value: "under review" },
    { label: "Confirmed", value: "confirmed" },
  ]

  Yup.addMethod(Yup.array, "uniqueProperty", function (propertyPath, message) {
    return this.test("unique", "", function (list) {
      const errors = []

      list.forEach((item, index) => {
        const propertyValue = _.get(item, propertyPath)

        if (
          propertyValue &&
          _.filter(list, [propertyPath, propertyValue]).length > 1
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  })

  function yupServiceTest() {
    // In this case, parent is the entire array
    const { parent } = this

    return (
      !!parent.serviceTypeSelection &&
      Object.keys(parent.serviceTypeSelection).length > 0
    )
  }

  const validationSchema = Yup.object().shape({
    referralEntity: Yup.object().shape({
      name: Yup.string().required("Please select the partner name"),
      noPrimaryPhone: Yup.bool(),
      primaryPhone: Yup.string().when("noPrimaryPhone", {
        is: false,
        then: Yup.string().required("Please specify a phone number"),
      }),
      noPrimaryEmail: Yup.bool(),
      primaryEmail: Yup.string().when("noPrimaryEmail", {
        is: false,
        then: Yup.string()
          .required("Please specify the e-mail address")
          .email("Please specify a valid e-mail address"),
      }),
    }),
    referralLocations: Yup.array()
      .of(
        Yup.object().shape({
          location: Yup.object().shape({
            name: Yup.string().required("Please enter the location name"),
            virtual: Yup.boolean(),
            noPhone: Yup.bool(),
            phone: Yup.string()
              .when("noPhone", {
                is: false,
                then: Yup.string().required("Please specify a phone number"),
              })
              .nullable(),
            noEmail: Yup.bool(),
            email: Yup.string().when("virtual", {
              is: false,
              then: Yup.string().when("noEmail", {
                is: false,
                then: Yup.string()
                  .required("Please specify the e-mail address")
                  .email("Please specify a valid e-mail address"),
              }),
              website: Yup.string()
                .when("virtual", {
                  is: true,
                  then: Yup.string().required(
                    "Please specify a website URL for this virtual location"
                  ),
                })
                .nullable(),
            }),
          }),
          serviceTypeSelection: Yup.object()
            .required("Please select at least one service")
            .test(
              "service selection test",
              "Please select at least one service",
              yupServiceTest
            ),
        })
      )
      .uniqueProperty("location.name", "Duplicate name"),
  })

  let referralEntitiesRoute = ROUTES.REFERRAL_ENTITIES

  if (
    location.pathname === ROUTES.PARTICIPANT_REFERRAL_ENTITIES_ADD ||
    location.pathname.startsWith(
      ROUTES.PARTICIPANT_REFERRAL_ENTITIES_EDIT.substring(
        0,
        ROUTES.PARTICIPANT_REFERRAL_ENTITIES_EDIT.lastIndexOf("/")
      )
    )
  ) {
    referralEntitiesRoute = ROUTES.PARTICIPANT_REFERRAL_ENTITIES
  }

  useEffect(() => {
    if (showChangeRequests) {
      setActiveTabIndex(changeRequestPanelIndex)
    }
  }, [showChangeRequests])

  useEffect(() => {
    if (entityId) {
      setIsLoading(true)

      const processResult = (result) => {
        result.referralLocations.forEach((loc) => {
          let location = loc
          let serviceTypeSelection = {}

          location.serviceTypes.forEach((service) => {
            let newService = {}
            newService[service.id] = {
              checked: true,
              partialChecked: false,
            }

            //Inject the object into serviceTypeSelection
            Object.assign(serviceTypeSelection, newService)
          })

          location.serviceTypeSelection = serviceTypeSelection
        })

        setReferralEntity(result)
        setChangeLog(undefined)

        setCanEdit(
          userCtx.session?.organizations?.some(
            (x) =>
              x.organization_id ===
              (result.referralEntity?.creationOrganizationId ||
                "No creator org")
          ) ||
            userCtx.session.user.staff_role === 1 ||
            userCtx.session.user.staff_role === 9
        )

        setIsLoading(false)
        setIsInitialLoad(false)
        setDoDisableForm(false)
      }

      if (entityId !== -1) {
        referralsApiReferralEntityGet(userCtx.token, entityId)
          .then((result) => {
            processResult(result)
          })
          .catch((error) => {
            LogError("ReferralEntityForm", error)
            userCtx.setNotificationError(RefGetErrorMessage(error))

            setIsLoading(false)
            setIsInitialLoad(false)
            setDoDisableForm(true)
          })
      } else if (userCtx.session.user.organization_type === "PARTNER") {
        referralsApiReferralEntityGetByOrganization(
          userCtx.token,
          userCtx.session.user.organization_id
        )
          .then((result) => {
            processResult(result)
          })
          .catch((error) => {
            LogError("ReferralEntityForm", error)
            userCtx.setNotificationError(RefGetErrorMessage(error))

            setIsLoading(false)
            setIsInitialLoad(false)
            setDoDisableForm(true)
          })
      }
    } else {
      setIsLoading(false)
      setIsInitialLoad(false)
      setDoDisableForm(false)
    }
    //Note: Do not add user as a dependency for this useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityId,
    userCtx.session.user.organization_id,
    userCtx.token,
    refreshProfile,
    triggerRefresh,
  ])

  useEffect(() => {
    refApiLocationTypesGet(userCtx.token)
      .then((result) => {
        setLocationTypes(result)
      })
      .catch((error) => {
        LogError("Unable to retrieve location types", error)
        userCtx.setNotificationError("Unable to retrieve location types")
        //setErrorMessage(RefGetErrorMessage(error))
      })

    refApiServiceCategoriesGet(userCtx.token)
      .then((result) => {
        let nodes = result.map((cat) => getServiceCategoryNode(cat))

        setServiceCategories(nodes)
      })
      .catch((error) => {
        LogError("Unable to retrieve service categories", error)
        userCtx.setNotificationError("Unable to retrieve service categories")
        //setErrorMessage(RefGetErrorMessage(error))
      })
    //Note: Do not add user as a dependency for this useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.token])

  useEffect(() => {
    if (serviceCategories.length > 0 && !!referralEntity) {
      if (referralEntity.referralLocations.length > 0) {
        //This recursive function will look into each option and through each child node to find the key
        const findPath = (key, options) => {
          let keyPath = []
          if (
            options?.some((option) => {
              if (option.key === key) {
                keyPath = option.path

                return true
              } else if (!!option.children) {
                let childPath = findPath(key, option.children)
                if (childPath?.length > 0) {
                  keyPath = childPath
                  return true
                } else {
                  return false
                }
              } else {
                return false
              }
            })
          ) {
            return keyPath
          }
        }

        referralEntity.referralLocations.forEach((location) => {
          let partialSelectOptions = []
          //foreach service type in this location, find the path for this key
          location.serviceTypes.forEach((serviceType) => {
            let path = findPath(serviceType.id, serviceCategories)

            if (!!path) {
              partialSelectOptions = [...partialSelectOptions, ...path]
            }
          })
          //Now that we have a list of parent paths, put the unique list that
          //is not yet in the serviceTypeSelection collection into the collection as partial selects
          new Set(partialSelectOptions).forEach((optionKey) => {
            if (
              !!optionKey &&
              !Object.keys(location.serviceTypeSelection).some(
                (type) => type === optionKey.toString()
              )
            ) {
              let newService = {}
              newService[optionKey] = {
                checked: false,
                partialChecked: true,
              }

              //Inject the object into serviceTypeSelection
              Object.assign(location.serviceTypeSelection, newService)
            }
          })
        })
      }
    }
  }, [serviceCategories, referralEntity])

  const [addOrg] = useMutation(ADD_ORGANIZATION, {
    variables: {
      uid: userCtx.session.user.staff_id,
    },
  })

  const [editOrg] = useMutation(EDIT_ORGANIZATION, {
    variables: {
      uid: userCtx.session.user.staff_id,
    },
  })

  function createReferralEntity(
    newReferralEntity,
    organization_id,
    admin_staff_id,
    isNewUser
  ) {
    let values = newReferralEntity
    //Add new user ID to users list.
    if (!!admin_staff_id) {
      values.userIds.push({
        uid: admin_staff_id,
        email: values.referralEntity.primaryEmail,
        isNewUser: isNewUser,
      })
    }
    values.referralEntity.defaultUserId = admin_staff_id
    values.referralEntity.organizationId = organization_id
    values.processingUserId = userCtx.session.user.staff_id
    values.processingOrganizationId = userCtx.session.user.organization_id
    //Create the referralEntity in the services database.
    refApiReferralEntityCreate(userCtx.token, values)
      .then((result) => {
        if (!result.referralEntitySuccess) {
          LogError("Unable to create partner profile", result)
          userCtx.setNotificationError(
            `Unable to create partner profile ${result.Message}`
          )
          setIsLoading(false)
          return
        } else if (result.message !== "") {
          LogError("partial failure of partner profile creation logged", result)
          userCtx.setNotificationError(result.message)
        }

        if (onEntityCreated) {
          const primaryLocation = values.referralLocations.filter(
            (x) =>
              x.location.id ===
              (!!selectedLocation
                ? selectedLocation
                : values.referralEntity.primaryLocationId)
          )

          values.referralEntity.id = result.referralEntityID
          values.password = ""
          values.additionalParameters = ""

          if (primaryLocation.length > 0) {
            values.referralLocation = primaryLocation[0]
          }

          //Set the ID to the newly created location Id.
          values.referralLocation.id = result.locations.find(
            (location) => location.name === values.referralLocation.name
          )?.id

          onEntityCreated(values)
        } else {
          navigate(referralEntitiesRoute)
        }
      })
      .catch((error) => {
        LogError("Unable to create partner profile", error)
        userCtx.setNotificationError(
          `Unable to create partner profile ${RefGetErrorMessage(error)}`
        )
        setIsLoading(false)
      })
  }

  const validateValues = async (profileId, name) => {
    try {
      const validation = await refApiReferralEntityValidate(
        userCtx.token,
        profileId,
        name
      )
      if (validation?.result === true) {
        return true
      } else {
        if (!!validation?.referralEntity) {
          setValidationProfile(validation.referralEntity)
          setShowValidationDialog(true)
        } else {
          LogError("Profile validation failed")
          userCtx.setNotificationError(
            `Profile validation failed: ${validation?.error}`
          )
        }

        setIsLoading(false)
        return false
      }
    } catch (error) {
      const msg = !!error.isAxiosError ? error.response.data : error.message

      LogError("Profile validation failed", msg)
      userCtx.setNotificationError(`Profile validation failed: ${msg}`)
      setIsLoading(false)
      return false
    }
  }

  async function createSoarOrganization(variables) {
    try {
      const addOrgResults = await addOrg({
        variables: variables,
      })

      if (
        addOrgResults.data.addOrganization === null ||
        addOrgResults.data.addOrganization?.organization_id === null
      ) {
        LogError(
          "An error occurred while creating the referral partner",
          addOrgResults
        )
        userCtx.setNotificationError(
          `An error occurred while creating the referral partner`
        )
        return undefined
      }

      return addOrgResults
    } catch (error) {
      LogError("An error occurred while creating the referral partner", error)
      userCtx.setNotificationError(
        `An error occurred while creating the referral partner`
      )
      return undefined
    }
  }

  async function handleFormSubmit(values) {
    //Fix location services
    values.referralLocations.forEach((location) => {
      let selection = []
      Object.keys(location.serviceTypeSelection).forEach((key) => {
        if (location.serviceTypeSelection[key].checked) {
          selection = [...selection, { id: key, Name: "_" }]
        }
      })
      location.serviceTypes = selection
    })

    if (!referralEntity.referralEntity.id) {
      setIsLoading(true)

      const orgType = partnerDefaultOrganizationType()
      const enterpriseId = partnerDefaultEnterpriseId()

      if (!orgType || !enterpriseId) {
        LogError("Default organization type and / or enterprise ID not found")
        userCtx.setNotificationError(
          `Default organization type and / or enterprise ID not found`
        )

        setIsLoading(false)
        return null
      }

      let primaryLocation = values.referralLocations.filter(
        (x) => x.location.id === values.referralEntity.primaryLocationId
      )
      if (primaryLocation.length > 0) {
        primaryLocation = primaryLocation[0].location
      } else {
        LogError("Primary location not found")
        userCtx.setNotificationError(`Primary location not found`)
        setIsLoading(false)
        return null
      }

      //Create new SOAR organization (This auto creates the default admin user)
      const mutateVars = getAddOrganizationVariables(
        userCtx,
        values.referralEntity.name,
        values.referralEntity.primaryEmail,
        primaryLocation.country
      )

      if (!!(await validateValues(0, values.referralEntity.name))) {
        if (
          !!values.referralEntity.primaryEmail &&
          !values.referralEntity.noPrimaryEmail
        ) {
          const addOrgResults = await createSoarOrganization(mutateVars)

          if (!!addOrgResults) {
            createReferralEntity(
              values,
              addOrgResults.data.addOrganization.organization_id,
              addOrgResults.data.addOrganization.organization_admins[0]
                .staff_id,
              addOrgResults.data.addOrganization.organization_admins[0]
                .isNewUser
            )
          } else {
            setIsLoading(false)
          }
        } else {
          createReferralEntity(values, null, null, null)
        }
      }
    } else {
      setIsLoading(true)

      let primaryLocation = values.referralLocations.filter(
        (x) => x.location.id === values.referralEntity.primaryLocationId
      )
      if (primaryLocation.length > 0) {
        primaryLocation = primaryLocation[0].location
      } else {
        LogError("Primary location not found")
        userCtx.setNotificationError(`Primary location not found`)
        setIsLoading(false)
        return null
      }

      if (
        !!(await validateValues(
          values.referralEntity.id,
          values.referralEntity.name
        ))
      ) {
        if (!saveChangeRequest) {
          editReferralEntity(values, primaryLocation)
        } else {
          await submitChangeRequests(values)
        }
      }
    }

    return true
  }

  const processProfileChangeRequests = (values) => {
    const entityValues = values.referralEntity
    const entityInitialValues = referralEntity.referralEntity

    let changeRequests = []
    //Find all fields and extract dirty values
    const fields = Object.keys(entityValues)
    const dirtyFields = fields.filter(
      (keyValue) => entityValues[keyValue] !== entityInitialValues[keyValue]
    )

    dirtyFields.forEach((key) => {
      changeRequests.push({
        fieldName: key,
        currentValue: entityInitialValues[key],
        newValue: entityValues[key],
      })
    })

    if (changeRequests.length > 0) {
      let payload = {
        referralLocationId: undefined,
        fields: changeRequests,
      }

      return payload
    } else {
      return undefined
    }
  }

  const processLocationChangeRequests = (values) => {
    let locationIds = [
      ...values.referralLocations?.map(
        (referralLocation) => referralLocation.location.id
      ),
    ]
    locationIds.push(
      ...referralEntity.referralLocations?.map(
        (referralLocation) => referralLocation.location.id
      )
    )
    locationIds = [...new Set(locationIds)]
    let locationChanges = []

    for (const locationId of locationIds) {
      const entityValues = values.referralLocations?.find(
        (referralLocation) => referralLocation.location.id === locationId
      )?.location
      const entityInitialValues = referralEntity.referralLocations?.find(
        (referralLocation) => referralLocation.location.id === locationId
      )?.location

      let changeRequests = []
      //Find all fields and extract dirty values
      const fields = Object.keys(entityValues).filter(
        (key) => key !== "referralEntity"
      )
      const dirtyFields = fields.filter(
        (keyValue) => entityValues[keyValue] !== entityInitialValues[keyValue]
      )

      dirtyFields.forEach((key) => {
        changeRequests.push({
          fieldName: key,
          currentValue: `${entityInitialValues[key]}`,
          newValue: `${entityValues[key]}`,
        })
      })

      if (changeRequests.length > 0) {
        let payload = {
          referralLocationId: locationId,
          fields: changeRequests,
        }

        locationChanges.push(payload)
      }
    }

    return locationChanges
  }

  const processLocationServicesChangeRequests = (values) => {
    const fieldName = "ServiceType"
    let locationIds = [
      ...values.referralLocations?.map(
        (referralLocation) => referralLocation.location.id
      ),
    ]
    locationIds.push(
      ...referralEntity.referralLocations?.map(
        (referralLocation) => referralLocation.location.id
      )
    )
    locationIds = [...new Set(locationIds)]

    for (const locationId of locationIds) {
      const entityValues = values.referralLocations?.find(
        (referralLocation) => referralLocation.location.id === locationId
      )?.serviceTypes
      const entityInitialValues = referralEntity.referralLocations?.find(
        (referralLocation) => referralLocation.location.id === locationId
      )?.serviceTypes

      let changeRequests = []

      //Find new types
      entityValues.forEach((type) => {
        if (!entityInitialValues.some((check) => `${check.id}` === type.id)) {
          changeRequests.push({
            fieldName: fieldName,
            currentValue: "",
            newValue: `${type.id}`,
          })
        }
      })

      //Find removed types
      entityInitialValues.forEach((type) => {
        if (!entityValues.some((check) => check.id === `${type.id}`)) {
          changeRequests.push({
            fieldName: fieldName,
            currentValue: `${type.id}`,
            newValue: "",
          })
        }
      })

      if (changeRequests.length > 0) {
        let payload = {
          referralLocationId: locationId,
          fields: changeRequests,
        }
        return payload
      } else {
        return undefined
      }
    }
  }

  const submitChangeRequests = async (values) => {
    try {
      let changeSets = []
      changeSets.push(processProfileChangeRequests(values))
      changeSets.push(...processLocationChangeRequests(values))
      changeSets.push(processLocationServicesChangeRequests(values))

      let allChanges = []

      changeSets.forEach((changeSet) => {
        if (!!changeSet) {
          allChanges.push(changeSet)
        }
      })

      if (allChanges.length > 0) {
        const payload = {
          referralEntityId: values.referralEntity.id,
          organizationId: userCtx.session.user.organization_id,
          organizationName: userCtx.session.user.organization_name,
          userId: userCtx.session.user.staff_id,
          userName: `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`,
          changeRequests: allChanges,
        }

        await refApiReferralEntityChangeRequest(userCtx.token, payload)
        onEntityCreated(values)
      } else {
        throw new Error("No changes found")
      }
    } catch (error) {
      LogError("Unable to create change request", error)
      userCtx.setNotificationError(RefGetErrorMessage(error))
      setIsLoading(false)
    }
  }

  const updateSoarOrganization = (referralEntity) => {
    editReferralEntity({ referralEntity: referralEntity }, undefined, true)
  }

  const editReferralEntity = async (
    values,
    primaryLocation,
    skipReferralsApiUpdate
  ) => {
    if (!!values.referralEntity.organizationId) {
      try {
        let variables = {
          staff_id: userCtx.session.user.staff_id,
          organization_id: values.referralEntity.organizationId,
          organization_name: getSOAROrganizationName(
            values.referralEntity.name
          ),
        }

        if (!!primaryLocation) {
          variables.organization_country = primaryLocation.country
          variables.organization_address = primaryLocation.address
          variables.organization_city = primaryLocation.city
          variables.organization_state = primaryLocation.state
          variables.organization_zip_code = primaryLocation.zip
        }

        // eslint-disable-next-line no-unused-vars
        const result = await editOrg({
          variables: variables,
        })

        values.processingUserId = userCtx.session.user.staff_id
        values.processingOrganizationId = userCtx.session.user.organization_id

        if (
          userCtx.session.user.organization_id ===
            values.referralEntity.organizationId &&
          values.referralEntity.name !== referralEntity?.referralEntity?.name
        ) {
          userCtx
            .changeOrganization(values.referralEntity.organizationId)
            .catch((error) => {
              LogError("Unable to update context organization profile", error)
              userCtx.setNotificationError(
                "Unable to update context organization profile"
              )
            })
        }
      } catch (error) {
        LogError("An error occurred while updating the referral partner", error)
        userCtx.setNotificationError(
          `An error occurred while updating the referral partner`
        )
        setIsLoading(false)
        return
      }
    } else if (
      !!values.referralEntity.primaryEmail &&
      !skipReferralsApiUpdate &&
      !values.referralEntity.organizationId
    ) {
      const mutateVars = getAddOrganizationVariables(
        userCtx,
        values.referralEntity.name,
        values.referralEntity.primaryEmail,
        primaryLocation.country
      )

      const orgCreation = await createSoarOrganization(mutateVars)

      if (!!orgCreation) {
        const admin_staff_id =
          orgCreation.data.addOrganization.organization_admins[0].staff_id
        values.referralEntity.organizationId =
          orgCreation.data.addOrganization.organization_id
        values.referralEntity.defaultUserId = admin_staff_id

        values.userIds.push({
          uid: admin_staff_id,
          email: values.referralEntity.primaryEmail,
        })

        const admins = orgCreation.data?.addOrganization?.organization_admins
        if (admins && admins?.length > 0 && admins[0]?.isNewUser) {
          values.additionalParameters = `{"password":"${mutateVars.staff_account_password}"}`
        }
      } else {
        setIsLoading(false)
        return
      }
    }

    if (!skipReferralsApiUpdate) {
      try {
        values.processingOrganizationId = userCtx.session.user.organization_id
        values.processingUserId = userCtx.session.user.staff_id

        await refApiReferralEntityUpdate(userCtx.token, values)

        if (!!onEntityCreated) {
          const primaryLocationArray = values.referralLocations.filter(
            (x) => x.location.id === values.referralEntity.primaryLocationId
          )

          if (primaryLocationArray?.length > 0) {
            values.referralLocation = primaryLocation[0]
          }
          values.password = ""

          onEntityCreated(values)
        } else {
          navigate(referralEntitiesRoute)
        }
      } catch (error) {
        LogError("Unable to edit organization", error)
        userCtx.setNotificationError(RefGetErrorMessage(error))
        //setNotification({Error: error, Header: "Error during submit"})
        setIsLoading(false)
      }
    }
  }

  function handleCancelClick() {
    if (onCancelClick) {
      onCancelClick()
    } else {
      navigate(referralEntitiesRoute)
    }
  }

  const getSaveButtonLabel = () => {
    let labelText = "Save"

    if (saveChangeRequest) {
      labelText = "Save change request"
    } else if (isNew && !hideHeader) {
      labelText = "Save and select"
    }

    return labelText
  }

  const GetFormField = ({ name, locationId, highBadge, children }) => {
    let changes = 0
    if (!!referralEntity.changeRequests) {
      if (locationId !== undefined) {
        changes = referralEntity.changeRequests.filter(
          (x) => x.fieldName === name && x.referralLocationId === locationId
        ).length
      } else {
        changes = referralEntity.changeRequests.filter(
          (x) => x.fieldName === name
        ).length
      }
    }

    return (
      <>
        <Tooltip target=".form-field-tt" />
        <div className={` ${refEntityStyles.divFormField} `}>
          {children}
          {changes > 0 ? (
            <div
              className={`form-field-tt ${refEntityStyles.changeRequestBadge} ${
                !!highBadge ? refEntityStyles.changeRequestBadgeHigh : ""
              }`}
              data-pr-tooltip={
                changes > 0
                  ? `There are ${changes} active change requests on this field. Click to view`
                  : ""
              }
              data-pr-position="left"
              onClick={() => {
                setActiveTabIndex(changeRequestPanelIndex)
              }}
            >
              <Badge value={changes} severity="warning" />
            </div>
          ) : null}
        </div>
      </>
    )
  }

  const ReportInvalidForm = ({ isValid, isValidating, isSubmitting }) => {
    if (!isValid && !isValidating && !isSubmitting && !reportedInvalidForm) {
      setReportedInvalidForm(true)
      userCtx.setNotificationError(
        "There were validation errors, please review the form and correct the fields indicated. "
      )
    }

    return <></>
  }

  const selectProfileFromValidation = (e) => {
    setValidationProfile(undefined)
    onEntityCreated(e)
    setActiveTabIndex(0)
  }

  if (entityId === -1 && userCtx.session.user.organization_type !== "PARTNER") {
    navigate(referralEntitiesRoute)
  }

  return (
    <div style={{ paddingBottom: "10px" }}>
      <ReferralEntityFormValidationDialog
        showDialog={showValidationDialog}
        validationPartnerProfile={validationProfile}
        onHideDialog={() => {
          setShowValidationDialog(false)
          setValidationProfile(undefined)
        }}
        onSelectProfile={(e) => {
          setShowValidationDialog(false)
          selectProfileFromValidation(e)
        }}
      />
      {!hideHeader ? <h2>Referral Partner Profile</h2> : null}
      <Formik
        initialValues={referralEntity}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await handleFormSubmit(values)
        }}
      >
        {({
          handleSubmit,
          isValidating,
          isValid,
          isSubmitting,
          dirty,
          values,
          setFieldValue,
          errors,
        }) => (
          <Form onSubmit={handleSubmit} autoComplete="off">
            <div>
              <TabView
                activeIndex={activeTabIndex}
                onTabChange={(e) => {
                  setActiveTabIndex(e.index)
                }}
              >
                <TabPanel
                  header={
                    errors.referralEntity === undefined ? (
                      "Profile"
                    ) : (
                      <span>
                        Profile <span style={{ color: "red" }}> [!]</span>
                      </span>
                    )
                  }
                >
                  <div className="p-fluid">
                    <ReferralEntityFormNameCheck
                      canEdit={canEdit}
                      onSelectProfile={selectProfileFromValidation}
                    >
                      <GetFormField name={"name"}>
                        <div
                          className={refEntityStyles.divPartnerNameContainer}
                        >
                          <div className={refEntityStyles.divPartnerName}>
                            <FormikTextInput
                              name="referralEntity.name"
                              label="Referral partner"
                              placeholderText="Referral partner name"
                              required={true}
                              isInitialLoad={isInitialLoad}
                              disabled={isLoading || doDisableForm || !canEdit}
                              helpText="The name of the referral partner"
                            />
                          </div>
                        </div>
                      </GetFormField>
                    </ReferralEntityFormNameCheck>

                    {isNew ? null : (
                      <GetFormField name={"fein"}>
                        <RefTextInput
                          name="referralEntity.fein"
                          label=" Federal Employer Identification Number"
                          placeholderText="Federal Employer Identification Number"
                          isInitialLoad={isInitialLoad}
                          disabled={isLoading || doDisableForm || !canEdit}
                          helpText="The federal employee identification number of the referral partner"
                        />
                      </GetFormField>
                    )}

                    <GetFormField name={"primaryContactName"}>
                      <RefTextInput
                        name="referralEntity.primaryContactName"
                        label="Referral partner primary contact name"
                        placeholderText="Primary contact name"
                        isInitialLoad={isInitialLoad}
                        disabled={isLoading || doDisableForm || !canEdit}
                        helpText="Name of the primary contact person"
                      />
                    </GetFormField>

                    <GetFormField name={"primaryLocationId"}>
                      <RefDropDown
                        name="referralEntity.primaryLocationId"
                        label="Primary location"
                        options={values.referralLocations.map((x) => {
                          return { name: x.location.name, id: x.location.id }
                        })}
                        optionLabel="name"
                        optionValue="id"
                        disabled={
                          isLoading ||
                          (!!values.referralEntity.id && !isEntityAdmin) ||
                          doDisableForm ||
                          !canEdit
                        }
                        isInitialLoad={isInitialLoad}
                        helpText="Primary location from where the referral partner operates. Please add locations under the referral locations tab to change this value."
                      />
                    </GetFormField>

                    <div className={`${refEntityStyles.webField}`}>
                      <div className={refEntityStyles.webInput}>
                        <GetFormField name={"website"}>
                          <RefTextInput
                            name="referralEntity.website"
                            label="Referral partner website"
                            required={false}
                            isInitialLoad={isInitialLoad}
                            disabled={isLoading || doDisableForm || !canEdit}
                            helpText="The web URL for the referral partner"
                            placeholderText="Website"
                          />
                        </GetFormField>
                      </div>
                      <div>
                        <Button
                          type="button"
                          className={`${refEntityStyles.webButton} ${refEntityStyles.button}`}
                          style={{ width: "40px" }}
                          onClick={() => {
                            let url = values.referralEntity.website

                            if (!url.includes("http")) {
                              url = `https://${url}`
                            }

                            window.open(url, "_blank", "noopener noreferrer")
                          }}
                          disabled={isLoading || !values.referralEntity.website}
                        >
                          <span className="material-icons">{"language"}</span>
                        </Button>
                      </div>
                    </div>

                    <RefCheckBox
                      name="referralEntity.noPrimaryEmail"
                      label="Referral partner has no e-mail address"
                      disabled={isLoading || doDisableForm || !canEdit}
                      helpText="If no e-mail address is available, the partner will not be able to manage their profile, referrals or receive notifications via RecoveryLink"
                    />

                    {!values.referralEntity.noPrimaryEmail ? (
                      <GetFormField name={"primaryEmail"}>
                        <FormikTextInput
                          name="referralEntity.primaryEmail"
                          label="Referral partner e-mail address"
                          placeholderText="E-mail address"
                          required={!values.referralEntity.noPrimaryEmail}
                          isInitialLoad={isInitialLoad}
                          disabled={isLoading || doDisableForm || !canEdit}
                          helpText="A default administrative user profile will be created using the Referral partner e-mail address."
                          onChangeCallback={(value) => {
                            if (
                              values.referralEntity.id === undefined &&
                              !!values.referralEntity.primaryLocationId
                            ) {
                              const primaryLocationIndex =
                                values.referralLocations.findIndex(
                                  (referralLocation) =>
                                    referralLocation.location.id ===
                                    values.referralEntity.primaryLocationId
                                )

                              if (
                                values.referralLocations[primaryLocationIndex]
                                  .location.email ===
                                values.referralEntity.primaryEmail
                              ) {
                                setFieldValue(
                                  `referralLocations[${primaryLocationIndex}].location.email`,
                                  value
                                )
                              }
                            }
                          }}
                        />
                      </GetFormField>
                    ) : null}

                    <RefCheckBox
                      name="referralEntity.noPrimaryPhone"
                      label="Referral partner has no phone number"
                      disabled={isLoading || doDisableForm || !canEdit}
                      helpText="Check this option if no telephone number is available for the partner."
                    />

                    {!values.referralEntity.noPrimaryPhone ? (
                      <GetFormField name={"primaryPhone"}>
                        <FormikTextInput
                          name="referralEntity.primaryPhone"
                          label="Referral partner phone number"
                          mask="(999) 999-9999"
                          required={true}
                          isInitialLoad={isInitialLoad}
                          disabled={isLoading || doDisableForm || !canEdit}
                          helpText="Primary contact telephone for the referral partner"
                          onChangeCallback={(value) => {
                            if (
                              values.referralEntity.id === undefined &&
                              !!values.referralEntity.primaryLocationId
                            ) {
                              const primaryLocationIndex =
                                values.referralLocations.findIndex(
                                  (referralLocation) =>
                                    referralLocation.location.id ===
                                    values.referralEntity.primaryLocationId
                                )

                              if (
                                values.referralLocations[primaryLocationIndex]
                                  .location.phone ===
                                values.referralEntity.primaryPhone
                              ) {
                                setFieldValue(
                                  `referralLocations[${primaryLocationIndex}].location.phone`,
                                  value
                                )
                              }
                            }
                          }}
                        />
                      </GetFormField>
                    ) : null}

                    {!values.referralEntity.notificationsEnabled ? (
                      <label
                        className={styles.errFieldMsg}
                        style={{ marginBottom: "10px" }}
                      >
                        Warning: New referrals or updates to existing referrals
                        could be missed with notifications disabled
                      </label>
                    ) : null}

                    {isNew ? null : (
                      <GetFormField name={"notificationsEnabled"}>
                        <RefCheckBox
                          name="referralEntity.notificationsEnabled"
                          label="Send referral notifications"
                          isInitialLoad={isInitialLoad}
                          disabled={isLoading || doDisableForm || !canEdit}
                          helpText="Should notifications of new referrals and referral updates be sent to the referral partner?"
                        />
                      </GetFormField>
                    )}

                    {referralEntity.referralEntity.id ? (
                      <GetFormField name={"entityStatus"}>
                        <RefDropDown
                          name="referralEntity.entityStatus"
                          label="Is the profile information correct?"
                          options={profileStateOptions}
                          disabled={
                            isLoading ||
                            !isEntityAdmin ||
                            doDisableForm ||
                            !canEdit
                          }
                        />
                      </GetFormField>
                    ) : null}
                  </div>
                </TabPanel>
                <TabPanel
                  header={
                    errors.referralLocations === undefined ? (
                      "Referral locations"
                    ) : (
                      <span>
                        Referral locations{" "}
                        <span style={{ color: "red" }}> [!]</span>
                      </span>
                    )
                  }
                >
                  <div className="p-fluid">
                    <ReferralEntityFormLocations
                      isInitialLoad={isInitialLoad}
                      values={values}
                      errors={errors}
                      isLoading={isLoading || isSubmitting}
                      isReadOnly={
                        isLoading || isSubmitting || doDisableForm || !canEdit
                      }
                      locationTypes={locationTypes}
                      serviceCategories={serviceCategories}
                      isNew={isNew && !hideHeader}
                      allowNewLocation={!saveChangeRequest}
                      returnLocationId={selectedLocation}
                      GetFormField={GetFormField}
                      onSetSelectedLocation={setSelectedLocation}
                      onShowChangeRequests={() => {
                        setActiveTabIndex(changeRequestPanelIndex)
                      }}
                    />
                  </div>
                </TabPanel>

                {!isPartnerUser &&
                !!values?.referralEntity?.id &&
                !!values?.referralEntity?.organizationId ? (
                  <TabPanel header="Users">
                    <ReferralEntityUserList
                      isDialog={true}
                      partnerOrganizationId={
                        values?.referralEntity?.organizationId
                      }
                      readOnly={!canEdit || !!saveChangeRequest}
                      partnerIsUnconfirmed={
                        values?.referralEntity?.entityStatus === "unconfirmed"
                      }
                      partnerCreationOrganization={
                        values?.referralEntity?.creationOrganizationId
                      }
                    />
                  </TabPanel>
                ) : null}

                {!!values?.referralEntity?.id &&
                (isEntityAdmin || saveChangeRequest) ? (
                  <TabPanel header={"Change requests"}>
                    <ReferralEntityFormChangeRequests
                      onUpdateChangeRequests={(requestList) => {
                        if (!!referralEntity?.changeRequests) {
                          const updatedEntity = { ...referralEntity }
                          updatedEntity.changeRequests = requestList
                          setReferralEntity(updatedEntity)
                        }
                      }}
                      changeRequests={referralEntity?.changeRequests}
                      referralEntityId={referralEntity?.referralEntity?.id}
                      onRefreshProfile={() => {
                        setRefreshProfile(!refreshProfile)
                      }}
                      onUpdateSoarOrganization={updateSoarOrganization}
                    />
                  </TabPanel>
                ) : null}

                <TabPanel header={"Change log"}>
                  <ReferralEntityFormChangeLog
                    referralEntityId={referralEntity?.referralEntity?.id}
                    changeLog={changeLog}
                    onSetChangeLog={setChangeLog}
                  />
                </TabPanel>
              </TabView>
            </div>

            <div className={refEntityStyles.footer}>
              <div
                className={
                  refEntityStyles.footer + " " + refEntityStyles.warningMsg
                }
              >
                {!values.referralEntity.notificationsEnabled ? (
                  <label className={styles.errFieldMsg}>
                    Warning: New referrals or updates to existing referrals
                    could be missed with notifications disabled
                  </label>
                ) : null}
              </div>

              <div
                className={
                  refEntityStyles.footer + " " + refEntityStyles.buttonContainer
                }
              >
                {canEdit ? (
                  <>
                    <ReportInvalidForm
                      isSubmitting={isSubmitting}
                      isValid={isValid}
                      isValidating={isValidating}
                    />
                    <Button
                      label={getSaveButtonLabel()}
                      icon="pi pi-check"
                      type="submit"
                      disabled={!dirty}
                      loading={isLoading || isInitialLoad}
                      style={{ marginRight: "0.5rem", minWidth: "160px" }}
                      className={refEntityStyles.button}
                      onClick={() => {
                        setReportedInvalidForm(false)
                      }}
                    />
                  </>
                ) : null}

                {!hideCancelButton ? (
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    type="button"
                    className={refEntityStyles.button}
                    onClick={() => {
                      handleCancelClick()
                    }}
                    disabled={isLoading || isInitialLoad}
                    style={{ width: "115px" }}
                  />
                ) : null}
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
